import { useEffect, useRef, useState } from 'react'

import debounce from '@core/lodash/debounce'

import {
  addActivityDetector,
  DEFAULT_ACTIVITY_EVENTS,
} from './addActivityDetector'

export const DEFAULT_TIMEOUT = 1000 * 60 * 10 // 10 minutes
const DEBOUNCE_TIMEOUT = 1000 // 1 second

/**
 * @param timeout The timeout in milliseconds before we set a user to inactive.
 *     Default to 1 minute.
 * @return True if the user triggers any interaction before the timeout.
 *     False if the user stays idle after the timeout.
 */
export function useDetectActivity(timeout = DEFAULT_TIMEOUT): boolean {
  const [isActive, setIsActive] = useState(true)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  )

  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      setIsActive(false)
    }, timeout)
    const unsubscribe = addActivityDetector(
      debounce(
        () => {
          setIsActive(true)
          clearTimeout(timeoutRef.current)
          timeoutRef.current = setTimeout(() => {
            setIsActive(false)
          }, timeout)
        },
        DEBOUNCE_TIMEOUT,
        {
          leading: true,
          maxWait: Math.max(timeout - 100, 100),
        },
      ),
      DEFAULT_ACTIVITY_EVENTS,
      false,
    )
    return () => {
      clearTimeout(timeoutRef.current)
      unsubscribe()
    }
  }, [timeout])

  return isActive
}
